import React, { useEffect, useMemo, useState } from 'react';
import { gameplayerAPI, matchplayersAPI, fetchPlayertoolsRoster } from '../../services/api';
import { parseLineupPasteNumberFirst, buildOwnTeamPastePreview } from '../../utils/playerListParser';
import {
    toStr,
    lineupPlayerKey,
    getLineupPlayerProfile,
    playerDisplayNameFromEntry,
    rosterEntryKey,
    lineupToRosterKey,
    isBetreuerEntry,
    rosterDisplayLabel,
    normalizePlayertoolEntry,
    matchPlayerToRosterEntry,
    getPlayertoolsProjectId,
    isOwnTeamRoster,
} from '../../utils/tickerLineupUtils';

const POSITION_OPTIONS = ['TW', 'LV', 'IV', 'RV', 'LM', 'ZM', 'DM', 'OM', 'RM', 'LA', 'RA', 'ST', 'HS'];

function PositionSelect({ entry, onUpdate }) {
    const stored = entry.positionDetail || (entry.field === 'N/A' ? '' : entry.field) || '';
    const [value, setValue] = useState(stored);

    useEffect(() => {
        setValue(stored);
    }, [entry._id, stored]);

    const save = async (nextValue) => {
        const normalized = nextValue.trim();
        if (normalized === stored) return;
        await onUpdate(entry._id, {
            positionDetail: normalized,
            field: normalized || 'N/A',
        });
    };

    return (
        <select
            className="lineup-pos-select"
            value={value}
            onChange={(e) => {
                setValue(e.target.value);
                save(e.target.value);
            }}
        >
            <option value="">—</option>
            {POSITION_OPTIONS.map((pos) => (
                <option key={pos} value={pos}>{pos}</option>
            ))}
            {stored && !POSITION_OPTIONS.includes(stored) && (
                <option value={stored}>{stored}</option>
            )}
        </select>
    );
}

function PlayerRow({ entry, availablePlayers, onRemove, onUpdate, bench = false }) {
    const profile = getLineupPlayerProfile(entry);
    const name = playerDisplayNameFromEntry(entry);
    const isStarter = entry.type !== 1;
    const key = lineupPlayerKey(entry);
    const roster = availablePlayers?.find((p) => rosterEntryKey(p) === key || toStr(p.plid?._id || p.plid) === toStr(entry.plid?._id || entry.plid));
    const num = entry.num ?? roster?.num ?? profile.num ?? '';

    return (
        <div className={`lineup-row ${bench ? 'lineup-row--bench' : ''}`}>
            <div className="lineup-jersey">{num || '–'}</div>
            <div className="lineup-name" title={name}>
                {name}
                {entry.isCaptain && <span className="lineup-captain">C</span>}
            </div>
            <PositionSelect entry={entry} onUpdate={onUpdate} />
            <div className="lineup-actions">
                <button
                    type="button"
                    className={`lineup-icon-btn lineup-icon-btn--captain ${entry.isCaptain ? 'lineup-icon-btn--active' : ''}`}
                    onClick={() => onUpdate(entry._id, { isCaptain: !entry.isCaptain })}
                    title="Kapitän"
                >C</button>
                <button
                    type="button"
                    className={`lineup-icon-btn lineup-icon-btn--starter ${isStarter ? 'lineup-icon-btn--active' : ''}`}
                    onClick={() => onUpdate(entry._id, { type: isStarter ? 1 : 0, isStarter: !isStarter })}
                    title={isStarter ? 'Auf dem Feld' : 'Bank'}
                >
                    {isStarter ? '▶' : '⬇'}
                </button>
                <button
                    type="button"
                    className="lineup-icon-btn lineup-icon-btn--danger"
                    onClick={() => onRemove(entry._id)}
                    title="Entfernen"
                >✕</button>
            </div>
        </div>
    );
}

function PlayerGroup({ title, players, availablePlayers, onRemove, onUpdate, bench = false }) {
    if (!players.length) return null;

    return (
        <div className="lineup-group">
            <div className="lineup-group-title">{title}</div>
            <div className="lineup-table-head">
                <span>Nr</span>
                <span>Spieler</span>
                <span>Pos</span>
                <span style={{ textAlign: 'right' }}>Aktion</span>
            </div>
            {players.map((entry) => (
                <PlayerRow
                    key={entry._id}
                    entry={entry}
                    availablePlayers={availablePlayers}
                    onRemove={onRemove}
                    onUpdate={onUpdate}
                    bench={bench}
                />
            ))}
        </div>
    );
}

function rosterLabel(entry) {
    return rosterDisplayLabel(entry);
}

function AddPlayerForm({
    teamId,
    teamName,
    projectId,
    trackedTeamId,
    existingKeys,
    onAdd,
    useMatchPlayers,
    onRosterRefresh,
    onMatchPlayersChange,
    rosterSeed = [],
}) {
    const rosterProjectId = getPlayertoolsProjectId(projectId);
    const ownTeam = isOwnTeamRoster(teamId, trackedTeamId);
    const [roster, setRoster] = useState([]);
    const [playerKey, setPlayerKey] = useState('');
    const [pos, setPos] = useState('');
    const [isStarter, setIsStarter] = useState(true);
    const [error, setError] = useState('');
    const [loadingRoster, setLoadingRoster] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [loadInfo, setLoadInfo] = useState('');
    const [quickName, setQuickName] = useState('');
    const [quickBusy, setQuickBusy] = useState(false);

    const loadRoster = async () => {
        if (!teamId) return;
        setLoadingRoster(true);
        setLoadError('');
        setLoadInfo('');
        try {
            let items = [];
            const tid = toStr(teamId);

            if (!ownTeam && useMatchPlayers) {
                const res = projectId
                    ? await matchplayersAPI.getAll({ pid: projectId })
                    : await matchplayersAPI.getAll({ tid });
                const mps = (res.data?.matchplayers || res.data || [])
                    .filter((mp) => toStr(mp.tid?._id || mp.tid) === tid);
                items = mps.map((mp) => matchPlayerToRosterEntry(mp, tid));
            } else {
                const { items: pts, source, url } = await fetchPlayertoolsRoster({ pid: rosterProjectId });
                let filtered = pts;
                const teamPts = pts.filter((pt) => toStr(pt.tid?._id || pt.tid) === tid);
                if (teamPts.length) filtered = teamPts;
                items = filtered.map((pt) => normalizePlayertoolEntry(
                    pt,
                    toStr(pt.tid?._id || pt.tid || teamId)
                ));
                setLoadInfo(filtered.length
                    ? `${filtered.length} via ${source}`
                    : `0 via ${source} · ${url}`);
            }

            setRoster(items);
        } catch (err) {
            console.error(err);
            setLoadError(err.message || err.response?.data?.message || 'Kader konnte nicht geladen werden');
            setRoster([]);
        } finally {
            setLoadingRoster(false);
        }
    };

    useEffect(() => {
        loadRoster();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamId, projectId, useMatchPlayers, ownTeam, trackedTeamId]);

    useEffect(() => {
        if (!roster.length && rosterSeed.length) {
            setRoster(rosterSeed);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rosterSeed.length, teamId]);

    const eligible = useMemo(() => roster.filter((entry) => {
        const key = rosterEntryKey(entry);
        if (!key) return false;
        if (isBetreuerEntry(entry)) return false;
        return !existingKeys.includes(key);
    }), [roster, existingKeys]);

    const handleAdd = () => {
        if (!playerKey) { setError('Bitte Spieler wählen'); return; }
        setError('');
        const entry = eligible.find((item) => rosterEntryKey(item) === playerKey)
            || roster.find((item) => rosterEntryKey(item) === playerKey);
        if (!entry) { setError('Spieler nicht gefunden'); return; }

        const payload = {
            num: entry.num ?? getLineupPlayerProfile(entry).num ?? null,
            field: pos || 'N/A',
            positionDetail: pos,
            type: isStarter ? 0 : 1,
            isStarter,
            tid: teamId,
        };

        if (!ownTeam && (useMatchPlayers || entry.source === 'matchplayer')) {
            payload.matchPlayerId = toStr(entry.matchPlayerId?._id || entry.matchPlayerId);
        } else {
            payload.plid = toStr(entry.plid?._id || entry.plid);
        }

        if (!payload.plid && !payload.matchPlayerId) {
            setError('Spieler-ID fehlt — Kader neu laden');
            return;
        }

        onAdd(payload);
        setPlayerKey('');
        setPos('');
        loadRoster();
    };

    const handleQuickOpponentAdd = async () => {
        const surname = quickName.trim();
        if (!surname || !projectId || !teamId) return;
        setQuickBusy(true);
        setError('');
        try {
            const res = await matchplayersAPI.create({
                pid: projectId,
                tid: teamId,
                surname,
                prename: '',
                num: null,
            });
            const mp = res.data;
            await onAdd({
                matchPlayerId: mp._id,
                num: null,
                field: pos || 'N/A',
                positionDetail: pos,
                type: isStarter ? 0 : 1,
                isStarter,
                tid: teamId,
            });
            setQuickName('');
            await loadRoster();
        } catch (err) {
            setError(err.response?.data?.message || 'Schnell-Hinzufügen fehlgeschlagen');
        } finally {
            setQuickBusy(false);
        }
    };

    const emptyHint = loadError
        || (ownTeam
            ? (roster.length ? 'Alle Spieler bereits in Aufstellung.' : `Liga-Kader leer (pid ${rosterProjectId})${loadInfo ? ` · ${loadInfo}` : ''}`)
            : 'Kein Gegner-Kader — oben importieren oder Notfall-Feld nutzen.');

    return (
        <div className="lineup-add">
            <div className="lineup-add-title">
                Spieler hinzufügen
                {!loadingRoster && (
                    <span className="lineup-add-count">
                        {eligible.length} verfügbar · {roster.length} im Kader
                    </span>
                )}
            </div>
            {!ownTeam && useMatchPlayers && (
                <p className="lineup-add-source">Gegner-Kader (Cup-Projekt)</p>
            )}
            {ownTeam && (
                <p className="lineup-add-source">Kader Liga · {roster.length} geladen{loadError ? ` · ${loadError}` : ''}</p>
            )}
            {error && <p className="lineup-add-error">{error}</p>}
            <div className="lineup-add-grid lineup-add-grid--select">
                <select
                    className="lineup-add-select"
                    value={playerKey}
                    disabled={loadingRoster}
                    onChange={(e) => { setPlayerKey(e.target.value); setError(''); }}
                >
                    <option value="">
                        {loadingRoster ? 'Kader wird geladen…' : `— ${teamName}: Spieler wählen —`}
                    </option>
                    {eligible.map((entry) => {
                        const key = rosterEntryKey(entry);
                        return (
                            <option key={key} value={key}>
                                {rosterLabel(entry)}
                            </option>
                        );
                    })}
                </select>
                <select
                    className="lineup-pos-select"
                    value={pos}
                    onChange={(e) => setPos(e.target.value)}
                >
                    <option value="">Pos</option>
                    {POSITION_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <label className="lineup-add-check">
                    <input type="checkbox" checked={isStarter} onChange={(e) => setIsStarter(e.target.checked)} />
                    Starter
                </label>
                <button type="button" className="lineup-btn lineup-btn--primary" onClick={handleAdd} disabled={!playerKey}>
                    + Add
                </button>
            </div>
            {!loadingRoster && eligible.length === 0 && (
                <p className="lineup-add-hint">{emptyHint}</p>
            )}
            <button type="button" className="lineup-add-reload" onClick={loadRoster} disabled={loadingRoster}>
                ↻ Kader neu laden
            </button>
            {!ownTeam && useMatchPlayers && (
                <div className="lineup-add-emergency">
                    <input
                        type="text"
                        className="lineup-add-search"
                        placeholder="Notfall: Gegner-Nachname"
                        value={quickName}
                        onChange={(e) => setQuickName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleQuickOpponentAdd()}
                    />
                    <button
                        type="button"
                        className="lineup-btn lineup-btn--ghost"
                        disabled={!quickName.trim() || quickBusy}
                        onClick={handleQuickOpponentAdd}
                    >
                        {quickBusy ? '…' : '+ Schnell'}
                    </button>
                </div>
            )}
        </div>
    );
}

function OwnTeamLineupTools({
    teamId,
    teamName,
    gameId,
    teamPlayertools,
    onLineupRefresh,
    onRosterRefresh,
    hasLineup,
}) {
    const [rawText, setRawText] = useState('');
    const [busy, setBusy] = useState('');
    const [message, setMessage] = useState('');
    const [importRows, setImportRows] = useState([]);

    const parsed = useMemo(() => parseLineupPasteNumberFirst(rawText), [rawText]);
    const preview = useMemo(
        () => buildOwnTeamPastePreview(parsed, teamPlayertools),
        [parsed, teamPlayertools]
    );

    const handlePasteImport = async () => {
        if (!parsed.length || !teamId) return;
        setBusy('import');
        setMessage('');
        setImportRows([]);
        try {
            const res = await gameplayerAPI.importOwnLineup({
                gid: gameId,
                tid: teamId,
                players: parsed,
                replace: true,
            });

            setImportRows(res.data?.rows || []);
            onRosterRefresh?.(res.data?.playertools || []);
            const fullLineup = await gameplayerAPI.getByGame(gameId);
            onLineupRefresh?.(fullLineup.data || []);

            const summary = res.data?.summary || {};
            const unmatched = summary.unmatched || 0;
            setMessage(
                `${summary.lineupCount || 0} in Aufstellung · `
                + `${summary.rosterAdded || 0} Kader aktualisiert`
                + (unmatched ? ` · ⚠️ ${unmatched} nicht erkannt (manuell hinzufügen)` : '')
            );
            setRawText('');
        } catch (err) {
            setMessage(err.response?.data?.message || 'Import fehlgeschlagen');
        } finally {
            setBusy('');
        }
    };

    const handleLoadFromRoster = async () => {
        if (!teamId || !teamPlayertools.length) return;
        setBusy('load');
        setMessage('');
        try {
            if (hasLineup && !window.confirm('Bestehende Aufstellung ersetzen?')) {
                setBusy('');
                return;
            }

            await gameplayerAPI.importOwnLineup({
                gid: gameId,
                tid: teamId,
                players: teamPlayertools.map((pt) => ({
                    fullName: `${pt.plid?.prename || ''} ${pt.plid?.surname || ''}`.trim(),
                    prename: pt.plid?.prename || '',
                    surname: pt.plid?.surname || '',
                    num: pt.num,
                    isStarter: true,
                })),
                replace: true,
            });

            const fullLineup = await gameplayerAPI.getByGame(gameId);
            onLineupRefresh?.(fullLineup.data || []);
            setMessage(`${teamPlayertools.length} Spieler aus Kader übernommen (Feine Pos. bitte setzen).`);
        } catch (err) {
            setMessage(err.response?.data?.message || 'Laden fehlgeschlagen');
        } finally {
            setBusy('');
        }
    };

    return (
        <details className="lineup-own-tools" open={!hasLineup}>
            <summary>📋 Kader einfügen (FCW / Portal)</summary>
            <div className="lineup-own-tools-body">
                <p className="lineup-own-tools-hint">
                    Liste einfügen – Abgleich nur mit bestehenden Spielern im System (Name).
                    Unbekannte Spieler werden <strong>nicht</strong> angelegt, sondern als Warnung angezeigt.
                    Diese danach manuell hinzufügen (z.&nbsp;B. bei geänderter Trikotnummer).
                </p>
                <textarea
                    className="lineup-own-textarea"
                    placeholder={'01\nNoah Ender\nTor\n0\n0\n0\n0'}
                    value={rawText}
                    onChange={(e) => setRawText(e.target.value)}
                />
                {preview.length > 0 && (
                    <div className="lineup-import-preview">
                        <p className="lineup-own-tools-hint">{preview.length} Spieler erkannt</p>
                        <ul className="lineup-import-preview-list">
                            {preview.slice(0, 8).map((row, idx) => (
                                <li key={`${row.fullName}-${idx}`}>
                                    <span className="lineup-import-preview-num">{row.num ?? '–'}</span>
                                    <span>{row.fullName || row.surname}</span>
                                    <span className={`lineup-import-preview-tag lineup-import-preview-tag--${row.status}`}>
                                        {row.message}
                                    </span>
                                </li>
                            ))}
                            {preview.length > 8 && (
                                <li className="lineup-own-tools-hint">… und {preview.length - 8} weitere</li>
                            )}
                        </ul>
                    </div>
                )}
                <div className="lineup-own-actions">
                    <button
                        type="button"
                        className="lineup-btn lineup-btn--primary"
                        disabled={!parsed.length || !!busy}
                        onClick={handlePasteImport}
                    >
                        {busy === 'import' ? 'Import…' : 'Abgleichen & Aufstellung'}
                    </button>
                    <button
                        type="button"
                        className="lineup-btn lineup-btn--ghost"
                        disabled={!!busy || !teamPlayertools.length}
                        onClick={handleLoadFromRoster}
                    >
                        {busy === 'load' ? 'Laden…' : 'Aus Kader übernehmen'}
                    </button>
                </div>
                {importRows.length > 0 && (
                    <ul className="lineup-import-preview-list">
                        {importRows.map((row, idx) => (
                            <li key={`result-${idx}`}>
                                <span>{row.fullName || row.surname}</span>
                                <span className={`lineup-import-preview-tag lineup-import-preview-tag--${row.status}`}>
                                    {row.message}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
                {message && <p className="lineup-message">{message}</p>}
            </div>
        </details>
    );
}

function OpponentLineupTools({ teamId, teamName, projectId, gameId, onImported, onLineupRefresh, hasLineup }) {
    const [rawText, setRawText] = useState('');
    const [busy, setBusy] = useState('');
    const [message, setMessage] = useState('');

    const parsed = useMemo(() => parseLineupPasteNumberFirst(rawText), [rawText]);

    const handlePasteImport = async () => {
        if (!parsed.length || !projectId || !teamId) return;
        setBusy('import');
        setMessage('');
        try {
            const res = await matchplayersAPI.bulkUpsert({
                pid: projectId,
                tid: teamId,
                players: parsed,
            });
            const lineupRes = await matchplayersAPI.applyLineup({
                gid: gameId,
                tid: teamId,
                matchPlayerIds: (res.data?.matchplayers || []).map((mp) => mp._id),
                replace: true,
            });
            onImported?.(res.data?.matchplayers || []);
            const fullLineup = await gameplayerAPI.getByGame(gameId);
            onLineupRefresh?.(fullLineup.data || []);
            setMessage(`${parsed.length} Spieler importiert, ${lineupRes.data?.count || 0} übernommen.`);
            setRawText('');
        } catch (err) {
            setMessage(err.response?.data?.message || 'Import fehlgeschlagen');
        } finally {
            setBusy('');
        }
    };

    const handleLoadPool = async () => {
        if (!projectId || !teamId) return;
        setBusy('load');
        setMessage('');
        try {
            const poolRes = await matchplayersAPI.getAll({ pid: projectId, tid: teamId });
            onImported?.(poolRes.data?.matchplayers || []);
            const res = await matchplayersAPI.applyLineup({ gid: gameId, tid: teamId });
            const fullLineup = await gameplayerAPI.getByGame(gameId);
            onLineupRefresh?.(fullLineup.data || []);
            setMessage(`${res.data?.count || 0} Spieler aus gespeichertem Kader übernommen.`);
        } catch (err) {
            setMessage(err.response?.data?.message || 'Laden fehlgeschlagen');
        } finally {
            setBusy('');
        }
    };

    return (
        <details className="lineup-opponent-tools" open={!hasLineup}>
            <summary>📋 Gegner-Kader importieren</summary>
            <div className="lineup-opponent-tools-body">
                <p className="lineup-opponent-tools-hint">
                    Liste aus dem Portal einfügen (RNr. zuerst). Positionen danach per Dropdown setzen.
                </p>
                <textarea
                    className="lineup-opponent-textarea"
                    placeholder={'01\nNoah Ender\nTor\n0\n0\n0\n0'}
                    value={rawText}
                    onChange={(e) => setRawText(e.target.value)}
                />
                {parsed.length > 0 && (
                    <p className="lineup-opponent-tools-hint">{parsed.length} Spieler erkannt</p>
                )}
                <div className="lineup-opponent-actions">
                    <button
                        type="button"
                        className="lineup-btn lineup-btn--primary"
                        disabled={!parsed.length || !!busy}
                        onClick={handlePasteImport}
                    >
                        {busy === 'import' ? 'Import…' : 'Einfügen & Aufstellung'}
                    </button>
                    <button
                        type="button"
                        className="lineup-btn lineup-btn--ghost"
                        disabled={!!busy}
                        onClick={handleLoadPool}
                    >
                        {busy === 'load' ? 'Laden…' : 'Gespeicherten Kader laden'}
                    </button>
                </div>
                {message && <p className="lineup-message">{message}</p>}
            </div>
        </details>
    );
}

function LineupTeamSection({
    teamId,
    teamName,
    trackedId,
    gameProjectId,
    gameId,
    lineup,
    availablePlayers,
    onLineupChange,
    onRosterRefresh,
    onMatchPlayersChange,
    onAdd,
    onUpdate,
    onRemove,
}) {
    const isOpponent = !isOwnTeamRoster(teamId, trackedId);
    const players = lineup.filter((e) => toStr(e.tid?._id || e.tid) === toStr(teamId));
    const starters = players.filter((p) => p.type !== 1);
    const subs = players.filter((p) => p.type === 1);
    const existingKeys = players.map((e) => lineupToRosterKey(e));
    const teamPlayertools = availablePlayers.filter(
        (entry) => toStr(entry.tid) === toStr(teamId)
            && entry.source !== 'matchplayer'
            && !isBetreuerEntry(entry)
    );
    const rosterSeed = availablePlayers.filter(
        (entry) => toStr(entry.tid) === toStr(teamId)
            && !isBetreuerEntry(entry)
            && (isOpponent ? entry.source === 'matchplayer' || entry.matchPlayerId : entry.source !== 'matchplayer')
    );

    return (
        <section className="lineup-team-card">
            <header className="lineup-team-head">
                <div className="lineup-team-title">
                    <span>{teamName}</span>
                    <span className={`lineup-team-badge ${isOpponent ? 'lineup-team-badge--opp' : 'lineup-team-badge--own'}`}>
                        {isOpponent ? 'Gegner' : 'Eigenes Team'}
                    </span>
                </div>
                <div className="lineup-team-stats">
                    <span className="lineup-stat-pill lineup-stat-pill--field">{starters.length} Feld</span>
                    <span className="lineup-stat-pill">{subs.length} Bank</span>
                </div>
            </header>

            {!isOpponent && (
                <OwnTeamLineupTools
                    teamId={teamId}
                    teamName={teamName}
                    gameId={gameId}
                    teamPlayertools={teamPlayertools}
                    hasLineup={players.length > 0}
                    onLineupRefresh={onLineupChange}
                    onRosterRefresh={(playertools) => onRosterRefresh?.(teamId, playertools)}
                />
            )}

            {isOpponent && (
                <OpponentLineupTools
                    teamId={teamId}
                    teamName={teamName}
                    projectId={gameProjectId}
                    gameId={gameId}
                    hasLineup={players.length > 0}
                    onImported={(matchPlayers) => onMatchPlayersChange?.(teamId, matchPlayers)}
                    onLineupRefresh={onLineupChange}
                />
            )}

            <div className="lineup-body">
                {!players.length && (
                    <div className="lineup-empty">
                        {isOpponent
                            ? 'Noch keine Aufstellung – Kader importieren oder Spieler hinzufügen.'
                            : 'Noch keine Aufstellung – Kader einfügen oder aus Kader übernehmen.'}
                    </div>
                )}

                <PlayerGroup
                    title="Startelf"
                    players={starters}
                    availablePlayers={availablePlayers}
                    onRemove={onRemove}
                    onUpdate={onUpdate}
                />
                <PlayerGroup
                    title="Ersatzbank"
                    players={subs}
                    availablePlayers={availablePlayers}
                    onRemove={onRemove}
                    onUpdate={onUpdate}
                    bench
                />

                <AddPlayerForm
                    teamId={teamId}
                    teamName={teamName}
                    projectId={gameProjectId}
                    trackedTeamId={trackedId}
                    existingKeys={existingKeys}
                    onAdd={onAdd}
                    useMatchPlayers={isOpponent}
                    onRosterRefresh={onRosterRefresh}
                    onMatchPlayersChange={onMatchPlayersChange}
                    rosterSeed={rosterSeed}
                />
            </div>
        </section>
    );
}

export default function LineupTab({
    game,
    gameId,
    projectId: projectIdProp,
    lineup,
    availablePlayers,
    onLineupChange,
    trackedTeamId,
    onMatchPlayersChange,
    onRosterRefresh,
}) {
    const [saving, setSaving] = useState(null);
    const [error, setError] = useState('');

    const homeId = toStr(game?.hid?._id || game?.hid);
    const awayId = toStr(game?.aid?._id || game?.aid);
    const homeName = game?.hid?.name || 'Heim';
    const awayName = game?.aid?.name || 'Gast';
    const gameProjectId = toStr(projectIdProp || game?.pid?._id || game?.pid);
    const trackedId = toStr(trackedTeamId);

    const handleAdd = async (teamId, data) => {
        setError('');
        setSaving('add');
        try {
            const payload = {
                gid: gameId,
                pid: gameProjectId,
                tid: teamId,
                num: data.num ?? null,
                field: data.field || 'N/A',
                positionDetail: data.positionDetail || '',
                type: data.type,
                isStarter: data.isStarter,
                isCaptain: data.isCaptain || false,
                minute: 0,
            };

            if (data.matchPlayerId) payload.matchPlayerId = data.matchPlayerId;
            else payload.plid = data.plid;

            const res = await gameplayerAPI.create(payload);
            onLineupChange((prev) => [...prev, res.data]);
        } catch (err) {
            setError(err.response?.data?.message || 'Fehler beim Hinzufügen');
        } finally {
            setSaving(null);
        }
    };

    const handleUpdate = async (id, updates) => {
        setSaving(id);
        onLineupChange((prev) => prev.map((e) => (e._id === id ? { ...e, ...updates } : e)));
        try {
            const res = await gameplayerAPI.update(id, updates);
            const updated = res.data?.data || res.data;
            if (updated?._id) {
                onLineupChange((prev) => prev.map((e) => (e._id === id ? { ...e, ...updated } : e)));
            }
        } catch (err) {
            setError('Fehler beim Aktualisieren');
            const lineupRes = await gameplayerAPI.getByGame(gameId);
            onLineupChange(lineupRes.data || []);
        } finally {
            setSaving(null);
        }
    };

    const handleRemove = async (id) => {
        if (!window.confirm('Spieler aus Aufstellung entfernen?')) return;
        setSaving(id);
        try {
            await gameplayerAPI.delete(id);
            onLineupChange((prev) => prev.filter((e) => e._id !== id));
        } catch (err) {
            setError('Fehler beim Entfernen');
        } finally {
            setSaving(null);
        }
    };

    return (
        <div className="lineup-root">
            {error && (
                <div className="lineup-alert">
                    <span>⚠️ {error}</span>
                    <button type="button" className="lineup-icon-btn lineup-icon-btn--danger" style={{ marginLeft: 'auto' }} onClick={() => setError('')}>✕</button>
                </div>
            )}
            {saving === 'add' && <div className="lineup-status">Speichern…</div>}

            <div className="lineup-grid">
                <LineupTeamSection
                    teamId={homeId}
                    teamName={homeName}
                    trackedId={trackedId}
                    gameProjectId={gameProjectId}
                    gameId={gameId}
                    lineup={lineup}
                    availablePlayers={availablePlayers}
                    onLineupChange={onLineupChange}
                    onRosterRefresh={onRosterRefresh}
                    onMatchPlayersChange={onMatchPlayersChange}
                    onAdd={(data) => handleAdd(homeId, data)}
                    onUpdate={handleUpdate}
                    onRemove={handleRemove}
                />
                <LineupTeamSection
                    teamId={awayId}
                    teamName={awayName}
                    trackedId={trackedId}
                    gameProjectId={gameProjectId}
                    gameId={gameId}
                    lineup={lineup}
                    availablePlayers={availablePlayers}
                    onLineupChange={onLineupChange}
                    onRosterRefresh={onRosterRefresh}
                    onMatchPlayersChange={onMatchPlayersChange}
                    onAdd={(data) => handleAdd(awayId, data)}
                    onUpdate={handleUpdate}
                    onRemove={handleRemove}
                />
            </div>
        </div>
    );
}
